<script lang="ts" setup>
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/vue";
import CourseCard from "./CourseCard.vue";
import { useCoursesStore } from "@/stores/coursesStore";
import { useUserStore } from "@/stores/userStore";
import AreaOfStudyModal from "@/components/Learn/AreaOfStudyModal.vue";
import type { CourseTab } from "@/stores/coursesStore";

const courseStore = useCoursesStore();
const { filteredCourses, isLoading, activeTab } = storeToRefs(courseStore);

const userStore = useUserStore();
const { user } = storeToRefs(userStore);

const allTabs: { key: CourseTab; label: string }[] = [
    { key: "all", label: "All Courses" },
    { key: "in-progress", label: "In Progress" },
    { key: "completed", label: "Completed" },
    { key: "draft", label: "Draft" },
];

const tabs = computed(() => {
    const hasPreviewContentPermission =
        user.value?.user?.permissions?.preview_content ?? false;
    return allTabs.filter(
        (tab) => tab.key !== "draft" || hasPreviewContentPermission
    );
});

const selectedIndex = computed(() =>
    tabs.value.findIndex((t) => t.key === activeTab.value)
);

function onTabChange(index: number) {
    activeTab.value = tabs.value[index].key;
}
</script>
<template>
	<div class="w-full flex flex-col">
		<AreaOfStudyModal />
    <div class="text-2xl font-bold text-gray-900 mb-6">
		<h1 class="inline">Learn</h1> — <h2 class="inline">Courses</h2>
    </div>
		<TabGroup :selected-index="selectedIndex" @change="onTabChange">
			<TabList class="flex border-b border-gray-200">
				<Tab
					v-for="tab in tabs"
					:key="tab.key"
					v-slot="{ selected }"
					as="template">
					<button
						class="px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px focus:outline-none"
						:class="
							selected
								? 'border-blue-600 text-blue-600'
								: 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
						">
						{{ tab.label }}
					</button>
				</Tab>
			</TabList>
			<TabPanels>
				<TabPanel
					v-for="tab in tabs"
					:key="tab.key"
					:unmount="false">
					<div class="pt-4">
						<p class="mb-4 whitespace-nowrap text-sm text-gray-600">
							{{ filteredCourses.length }} Courses
						</p>
						<div
							v-if="isLoading"
							class="flex w-full h-full items-center justify-center">
							<LoadingSpinner :show="isLoading" />
						</div>
						<div class="grid-cols-250 grid gap-6">
							<div
								v-for="courseEnrollment in filteredCourses"
								:key="courseEnrollment.course.id">
								<CourseCard
									:course="courseEnrollment.course"
									:status="courseEnrollment?.enrollment?.status ?? 'new'" />
							</div>
						</div>
					</div>
				</TabPanel>
			</TabPanels>
		</TabGroup>
	</div>
</template>
